/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	Modal,
	Button,
	Group,
	Text,
	Box,
	ActionIcon,
	Alert,
} from '@mantine/core';

// Icons
import {
	IconX,
	IconTrash,
	IconInfoCircle,
	IconDownload,
	IconCheck,
} from '@tabler/icons-react';

// Utils
import {
	addZeroToTenPlace,
	truncateString,
} from '@utils/functions';

// Styles
import classes from './MultipleSelectionApiTokenModal.module.css';
import clsx from 'clsx';

import { Tooltip } from '@components/atoms/Tooltip';

// I18n
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const MultipleSelectionApiTokenModal = (props: {
	onConfirm: () => void;
	onClose: () => void;
	onRemove: (data: any) => void;
	opened: boolean;
	data: any;
	type: string;
}) => {
	/* ******************************* Hooks ****************************** */
	const { t } = useTranslation();

	/* ******************************* Constants ****************************** */
	const NBR_TOTAL_ITEMS = props.data.length;
	const NBR_DATA_NOT_VERIFIED = props.data.filter(
		(item: any) => !item.verified
	).length;

	const NBR_DATA_VERIFIED =
		NBR_TOTAL_ITEMS - NBR_DATA_NOT_VERIFIED;

	/* ******************************** Helpers ******************************* */
	const isNotValidToConfirm = () => {
		switch (props.type) {
			case 'verify':
				if (NBR_DATA_NOT_VERIFIED == 0) return true;
				break;
			case 'delete':
				if (NBR_DATA_NOT_VERIFIED == 0) return true;
				break;
			default:
				return false;
		}
	};

	const getConfirmButton = () => {
		switch (props.type) {
			case 'delete':
				return (
					<Button
						data-testid="A1FtGXRw_F5jdHi6B-button"
						className={classes.button}
						size="sm"
						color="error"
						onClick={() => {
							props.onConfirm();
						}}
						disabled={
							props.data.length == 0 ||
							isNotValidToConfirm()
						}
						leftSection={<IconTrash size={18} />}
					>
						{t('global.delete')} api tokens
					</Button>
				);

			case 'verify':
				return (
					<Button
						data-testid="A2FtGXRw_F5jdHi8B-button"
						leftSection={<IconCheck size={18} />}
						className={classes.button}
						size="sm"
						onClick={() => {
							props.onConfirm();
						}}
						disabled={
							props.data.length == 0 ||
							isNotValidToConfirm()
						}
					>
						Verify api tokens
					</Button>
				);
			case 'download':
				return (
					<Button
						data-testid="A3FtGXRw_F0jdHi8B-button"
						leftSection={<IconDownload size={18} />}
						className={classes.button}
						size="sm"
						onClick={() => {
							props.onConfirm();
						}}
						disabled={
							props.data.length == 0 ||
							isNotValidToConfirm()
						}
					>
						Download api tokens
					</Button>
				);

			default:
				break;
		}
	};

	const getAlert = () => {
		switch (props.type) {
			case 'delete':
				return (
					<Alert
						icon={<IconInfoCircle />}
						variant="light"
						color="error"
						title={t('productions.alert.delete.title')}
					>
						{NBR_DATA_VERIFIED}{' '}
						{t(
							'productions.alert.delete.itemsAlreadyVerified'
						)}
					</Alert>
				);

			case 'verify':
				return (
					<Alert
						icon={<IconInfoCircle />}
						variant="light"
						color="blue"
						title={t('productions.alert.verify.title')}
					>
						{NBR_DATA_VERIFIED}{' '}
						{t(
							'productions.alert.verify.itemsAlreadyVerified'
						)}
					</Alert>
				);
			case 'download':
				return null;

			default:
				break;
		}
	};

	return (
		<Modal
			opened={props.opened}
			onClose={props.onClose}
			classNames={classes}
			withCloseButton={false}
			size={500}
		>
			<Text className={classes.title}>
				{addZeroToTenPlace(NBR_TOTAL_ITEMS)}{' '}
				{t('global.selected')} api token
			</Text>
			<Box className={classes.customBody}>
				<Box className={classes.header}>
					<Box className={classes.columnHeader}>
						{t('global.name')}
					</Box>
					<Box className={classes.columnHeader}>
						{t('global.expirationDate')}
					</Box>
					<Box className={classes.columnHeader}>
						{t('global.entity')}
					</Box>
					<Box className={classes.columnCancel}></Box>
				</Box>
				{props.data.map((item: any) => (
					<Box
						key={item.id}
						className={clsx(
							classes.row,
							item.verified &&
								props.type != 'download' &&
								classes.rowInvalideVerify
						)}
					>
						<Tooltip
							label={item.name}
							isActive={item.name?.length > 17}
						>
							<Box className={classes.column}>
								{truncateString(item.name, 17)}
							</Box>
						</Tooltip>
						<Box className={classes.column}>
							{truncateString(item.expiredAt, 17)}
						</Box>
						<Tooltip
							label={item.producer}
							isActive={item.producer?.length > 17}
						>
							<Box className={classes.column}>
								{truncateString(item.producer, 17)}
							</Box>
						</Tooltip>
						<Box className={classes.columnCancel}>
							<ActionIcon
								data-testid={item.id}
								variant="transparent"
								onClick={() => {
									props.onRemove(item);
								}}
							>
								<IconX
									color="var(--mantine-color-grey)"
									size={'15px'}
								/>
							</ActionIcon>
						</Box>
					</Box>
				))}
			</Box>
			<Box
				style={{
					marginTop: '10px',
					marginRight: '15px',
				}}
			>
				{NBR_DATA_VERIFIED != 0 && getAlert()}
			</Box>
			<Box className={classes.containerButtons}>
				<Group mt="xl">
					<Button
						data-testid="DCCtGlwC_F5jdHi6B-button"
						size="sm"
						color="gray"
						variant="transparent"
						onClick={() => {
							props.onClose();
						}}
						className={classes.cancel}
					>
						{t('global.cancel')}
					</Button>
					{getConfirmButton()}
				</Group>
			</Box>
		</Modal>
	);
};
